import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Bounce, toast } from 'react-toastify'

import type { ToggleSource } from '@livekit/components-core'
import { setupMediaToggle, setupManualToggle, log } from '@livekit/components-core'
import { Track } from 'livekit-client'
import type { ScreenShareCaptureOptions } from 'livekit-client'

import { clientLogError, clientLogInfo } from '@/src/lib/clientLog'
import { getScreenShareTrackPublishOptions } from '@/src/lib/screenShareOptions'
import { useScreenShareProfile } from '@/src/context/screenShareProfile-context'

import type { TrackToggleProps } from '@/src/components'
import { Toast } from '@/src/components/Toast/Toast'

import { useMaybeRoomContext } from '@/src/context'
import { ESpecificErrors } from '@/src/constants/constants'

import { mergeProps } from '@/src/mergeProps'

import { useObservableState } from '@/src/hooks/internal'
import { useNavigator } from '@/src/hooks/media/useNavigator'

/** @public */
export interface UseTrackToggleProps<T extends ToggleSource>
  extends Omit<TrackToggleProps<T>, 'showIcon'> {
  isPiP?: boolean
}

interface IScreenShare {
  isPending: boolean
  isEnabled: boolean
}

const initialScreenShare: IScreenShare = {
  isPending: false,
  isEnabled: false,
}

/**
 * The `useTrackToggle` hook is used to implement the `TrackToggle` component and returns state
 * and functionality of the given track.
 *
 * @example
 * ```tsx
 * const { buttonProps, enabled } = useTrackToggle(trackRef);
 * return <button {...buttonProps}>{enabled ? 'disable' : 'enable'}</button>;
 * ```
 * @public
 */
export function useTrackToggle<T extends ToggleSource>({
  source,
  onChange,
  initialState,
  captureOptions,
  isPiP = false,
  publishOptions,
  onDeviceError,
  ...rest
}: UseTrackToggleProps<T>) {
  const { t } = useTranslation()
  const room = useMaybeRoomContext()
  const defaultProfile = useScreenShareProfile()
  const track = room?.localParticipant?.getTrackPublication(source)
  /** `true` if a user interaction such as a click on the TrackToggle button has occurred. */
  const userInteractionRef = useRef(false)

  // Поток должен переживать рендеры, иначе кнопка остановки теряет захваченные треки.
  const screenStreamRef = useRef<MediaStream | null>(null)
  const screenSharePendingRef = useRef(false)
  const screenShareGenerationRef = useRef(0)

  const [screenShare, setScreenShare] = useState<IScreenShare>(initialScreenShare)

  const { toggle, className, pendingObserver, enabledObserver } = useMemo(
    () =>
      room
        ? setupMediaToggle<T>(source, room, captureOptions, publishOptions, onDeviceError)
        : setupManualToggle(),
    [room, source, captureOptions, publishOptions],
  )

  /** Останавливает захват сразу, не дожидаясь сетевого снятия публикации. */
  const stopScreenShare = useCallback(() => {
    const stream = screenStreamRef.current

    screenShareGenerationRef.current += 1
    screenStreamRef.current = null
    setScreenShare((prev) => ({ ...prev, isEnabled: false }))

    stream?.getTracks().forEach((screenTrack) => {
      screenTrack.stop()
      void room?.localParticipant.unpublishTrack(screenTrack).catch((error) => {
        clientLogError(`Ошибка при остановке демонстрации экрана: ${String(error)}`)
      })
    })
  }, [room])

  /** Захватывает экран в пределах профиля и публикует единственный исходный слой. */
  const startScreenShare = useCallback(async () => {
    if (!room || screenSharePendingRef.current || screenStreamRef.current) {
      return null
    }

    const generation = ++screenShareGenerationRef.current
    const options = captureOptions as ScreenShareCaptureOptions | undefined
    const resolution = options?.resolution ?? defaultProfile.captureOptions.resolution
    const videoConstraints: MediaTrackConstraints = {
      width: { max: resolution.width },
      height: { max: resolution.height },
      frameRate: { max: resolution.frameRate },
    }
    const displayOptions = {
      audio: options?.audio ?? true,
      video: videoConstraints,
      selfBrowserSurface: options?.selfBrowserSurface,
    }

    screenSharePendingRef.current = true
    setScreenShare((prev) => ({ ...prev, isPending: true }))

    // Шаг фиксируем до try: в catch по нему видно, где именно сорвалось —
    // захват, применение ограничений или сама публикация
    let step: 'capture' | 'constrain' | 'publish' = 'capture'
    let requestedCodec: string | null = null

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia(displayOptions)

      // Закрытие комнаты во время браузерного диалога не должно оставлять живой захват.
      if (generation !== screenShareGenerationRef.current) {
        stream.getTracks().forEach((screenTrack) => screenTrack.stop())

        return null
      }

      screenStreamRef.current = stream

      const screenVideo = stream.getVideoTracks()[0]

      if (!screenVideo) {
        throw new Error('Браузер не вернул видеотрек демонстрации экрана')
      }

      step = 'constrain'

      screenVideo.addEventListener('ended', stopScreenShare, { once: true })

      const settings = screenVideo.getSettings()

      // Не публикуем неограниченный original, если браузер проигнорировал пределы захвата.
      if (
        settings.width > resolution.width ||
        settings.height > resolution.height ||
        settings.frameRate > resolution.frameRate
      ) {
        await screenVideo.applyConstraints(videoConstraints)
      }

      const constrainedSettings = screenVideo.getSettings()

      if (
        !constrainedSettings.width ||
        !constrainedSettings.height ||
        constrainedSettings.width > resolution.width ||
        constrainedSettings.height > resolution.height ||
        constrainedSettings.frameRate > resolution.frameRate
      ) {
        throw new Error('Браузер не применил ограничения демонстрации экрана')
      }

      step = 'publish'

      for (const screenTrack of stream.getTracks()) {
        if (generation !== screenShareGenerationRef.current || screenTrack.readyState === 'ended') {
          break
        }

        const screenPublishOptions = screenTrack.kind === Track.Kind.Video
          ? getScreenShareTrackPublishOptions(
              room.options.publishDefaults,
              { ...defaultProfile.publishOptions, ...publishOptions },
            )
          : { source: Track.Source.ScreenShareAudio }

        const publication = await room.localParticipant.publishTrack(screenTrack, screenPublishOptions)

        // Пишем, что именно мы попросили у SDK: согласованный кодек и профиль
        // видно уже только в снимке screen-share-sender, а здесь остаётся запрос.
        // Без него невозможно понять, кто выбрал H.264 — мы или настройки комнаты
        if (screenTrack.kind === Track.Kind.Video) {
          requestedCodec = screenPublishOptions.videoCodec ?? null

          clientLogInfo('screen-share-publish', {
            source: Track.Source.ScreenShare,
            requestedCodec,
            resolution: `${resolution.width}x${resolution.height}`,
            trackSid: publication.trackSid,
          })
        }

        // Пользователь мог остановить экран, пока SDK согласовывал публикацию.
        if (generation !== screenShareGenerationRef.current) {
          await room.localParticipant.unpublishTrack(screenTrack)
        }
      }

      if (generation === screenShareGenerationRef.current) {
        setScreenShare((prev) => ({ ...prev, isEnabled: true }))
      }
    } catch (error) {
      stopScreenShare()
      console.error('Ошибка при запуске демонстрации экрана:', error)
      clientLogError(`Ошибка при запуске демонстрации экрана: ${String(error)}`, {
        source: Track.Source.ScreenShare,
        step,
        requestedCodec,
      })
    } finally {
      screenSharePendingRef.current = false
      setScreenShare((prev) => ({ ...prev, isPending: false }))
    }

    return screenStreamRef.current
  }, [room, captureOptions, publishOptions, stopScreenShare, defaultProfile])

  useEffect(() => () => {
    screenShareGenerationRef.current += 1

    const stream = screenStreamRef.current

    screenStreamRef.current = null
    stream?.getTracks().forEach((screenTrack) => screenTrack.stop())
  }, [room])

  const pending = useObservableState(pendingObserver, false)
  const enabled = useObservableState(enabledObserver, initialState ?? !!track?.isEnabled)

  const isEnabled = source === Track.Source.ScreenShare ? screenShare.isEnabled : enabled
  const isPending = source === Track.Source.ScreenShare ? screenShare.isPending : pending

  useEffect(() => {
    onChange?.(isEnabled, userInteractionRef.current)
    userInteractionRef.current = false
  }, [isEnabled, onChange])

  useEffect(() => {
    if (initialState !== undefined) {
      log.debug('forcing initial toggle state', source, initialState)
      toggle(initialState)
    }
  }, [])

  const { cameraAccess, microphoneAccess } = useNavigator()

  useLayoutEffect(() => {
    if (!room) return

    // Для того, чтобы не было задержки включении микрофона при первом нажатии, а также для того, чтобы у нас автоматически открывался PiP, включаем в первый раз его принудительно, а затем сразу же переключаем обратно
    const checkMicrophone = async () => {
      if (room && source === Track.Source.Microphone && !isPiP) {
        try {
          await toggle().then(async () => {
            await toggle()
          })
        } catch (e) {
          console.error(`Error with toggling Microphone: \n${e}`)
        }
      }
    }

    void checkMicrophone()
  }, [room, source, isPiP])

  const newProps = useMemo(() => {
    return mergeProps(rest, { className })
  }, [rest, className])

  const clickHandler: MouseEventHandler<HTMLButtonElement> = useCallback(
    async () => {
      userInteractionRef.current = true

      // Для ScreenSharing пришлось написать свою реализацию, так как плашка о том, что транслируется вкладка не закрывалась.
      // Это не получилось решить средствами библиотеки livekit, поэтому написал свою реализацию
      if (source === Track.Source.ScreenShare) {
        if (screenStreamRef.current) {
          stopScreenShare()
        } else {
          await startScreenShare()
        }

        userInteractionRef.current = false
      } else {
        if (source === Track.Source.Microphone && room) {
          const localPermissions = room.localParticipant?.permissions
          const canPublish = localPermissions?.canPublish
          const canPublishSources = localPermissions?.canPublishSources
          const canPublishMicrophone = !canPublishSources?.length || canPublishSources.includes(2)

          if (!canPublish || !canPublishMicrophone) {
            toast.error(
              (content) => (
                <Toast title={content?.toastProps?.ariaLabel} description={content?.data} />
              ),
              {
                ariaLabel: t('toast.error'),
                data: t('microphonePermissionDenied'),
                role: 'alert',
                position: 'bottom-right',
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: 'dark',
                transition: Bounce,
              },
            )
            userInteractionRef.current = false
            return
          }
        }

        await toggle()
          .catch((err) => {
            const error: Error = err

            if (source === Track.Source.Camera) {
              const cameraUsedInAnotherApp = error?.message?.includes(
                ESpecificErrors['Camera already used in another app'],
              )

              if (cameraUsedInAnotherApp) {
                toast.error(
                  (content) => (
                    <Toast title={content?.toastProps?.ariaLabel} description={content?.data} />
                  ),
                  {
                    ariaLabel: t('toast.error'),
                    data: t('cameraUsedInAnotherApp'),
                    role: 'alert',
                    position: 'bottom-right',
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                    theme: 'dark',
                    transition: Bounce,
                  },
                )
              }
            }

            return err
          })
          .finally(() => (userInteractionRef.current = false))
      }
    },
    [toggle, source, startScreenShare, stopScreenShare, room, t],
  )

  // Если у пользователь нет разрешения на использование камеры/микрофона, мы выключаем их
  useLayoutEffect(() => {
    /* 
     Строгая проверка на cameraAccess === false нужна, так как до проверки пермишенов пользователя начальное состояние cameraAccess это null и проверка !cameraAccess в таком случае не подойдет */
    if (cameraAccess === false && source === Track.Source.Camera) {
      toggle(false)
    }

    /* 
     Строгая проверка на microphoneAccess === false нужна, так как до проверки пермишенов пользователя начальное состояние microphoneAccess это null и проверка !microphoneAccess в таком случае не подойдет */
    if (microphoneAccess === false && source === Track.Source.Microphone) {
      toggle(false)
    }
  }, [cameraAccess, microphoneAccess, source])

  return {
    toggle,
    enabled: isEnabled,
    pending: isPending,
    track,
    buttonProps: {
      ...newProps,
      'aria-pressed': isEnabled,
      'data-lk-source': source,
      'data-lk-enabled': isEnabled,
      disabled: isPending,
      onClick: clickHandler,
    } as React.ButtonHTMLAttributes<HTMLButtonElement>,
  }
}
